// RoomPlayer.jsx — Synced player UI (drop-in for RoomPage left column)
// Props: currentMoment, isPlaying, currentTime, duration, ready, isHost,
//        currentIndex, queueLength, onTogglePlay, onSeek, onPrev, onNext

export default function RoomPlayer({
  currentMoment,
  isPlaying,
  currentTime,
  duration,
  ready,
  isHost,
  currentIndex,
  queueLength,
  onTogglePlay,
  onSeek,
  onPrev,
  onNext,
}) {
  function formatTime(ms) {
    const s = Math.floor(typeof ms === 'number' && ms > 1000 ? ms / 1000 : ms);
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
  }

  const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0;

  if (!currentMoment) return (
    <div style={styles.empty}>
      <div style={styles.emptyIcon}>♪</div>
      <p style={styles.emptyTitle}>Queue is empty</p>
      <p style={styles.emptySubtitle}>Add moments from the feed to get started!</p>
    </div>
  );

  return (
    <div style={styles.wrapper}>
      <style>{`
        @keyframes rp-pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(212,83,126,0.4); }
          50%       { box-shadow: 0 0 0 10px rgba(212,83,126,0); }
        }
        @keyframes rp-spin {
          to { transform: rotate(360deg); }
        }
        .rp-play-btn { transition: transform 0.12s, opacity 0.12s; }
        .rp-play-btn:hover:not(:disabled) { transform: scale(1.07); }
        .rp-skip-btn { transition: opacity 0.15s, transform 0.12s; }
        .rp-skip-btn:hover:not(:disabled) { transform: scale(1.1); opacity: 1 !important; }
        .rp-range::-webkit-slider-thumb {
          -webkit-appearance: none;
          width: 14px; height: 14px;
          border-radius: 50%;
          background: #D4537E;
          cursor: pointer;
          margin-top: -5px;
          box-shadow: 0 0 6px rgba(212,83,126,0.6);
        }
        .rp-range::-webkit-slider-runnable-track {
          height: 4px;
          background: transparent;
          border-radius: 2px;
        }
        .rp-range { cursor: pointer; }
        .rp-range:disabled { cursor: default; }
      `}</style>

      {/* Album art */}
      <div style={styles.artWrap}>
        {currentMoment.album_image
          ? <img src={currentMoment.album_image} alt="" style={styles.art} />
          : <div style={styles.artFallback}>♪</div>
        }

        {/* Overlay: playing indicator */}
        {isPlaying && ready && (
          <div style={styles.playingBadge}>
            <span style={styles.playingDot} />
            Live
          </div>
        )}

        {isHost && (
          <div style={styles.hostBadge}>Host</div>
        )}

        {currentMoment.addedBy && (
          <div style={styles.addedByBadge}>Added by @{currentMoment.addedBy}</div>
        )}
      </div>

      {/* Track info */}
      <div style={styles.infoRow}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={styles.trackName}>{currentMoment.track_name}</div>
          <div style={styles.artistName}>{currentMoment.artist_name}</div>
          {currentMoment.timestamp_ms > 0 && (
            <div style={styles.timestampBadge}>
              ⏱ Starting from {formatTime(currentMoment.timestamp_ms)}
            </div>
          )}
        </div>
        {/* Track counter */}
        <div style={styles.trackCounter}>
          {currentIndex + 1} / {queueLength}
        </div>
      </div>

      {/* Progress */}
      <div style={styles.progressWrap}>
        {/* Visual bar */}
        <div style={styles.progressTrack}>
          <div style={{ ...styles.progressFill, width: `${progressPercent}%` }} />
          {/* Thumb dot */}
          <div style={{ ...styles.progressThumb, left: `calc(${progressPercent}% - 6px)` }} />
        </div>

        {/* Invisible range input on top (host only) */}
        {isHost && (
          <input
            type="range"
            className="rp-range"
            min="0"
            max={duration || 300}
            value={currentTime}
            onChange={onSeek}
            style={styles.rangeInput}
          />
        )}

        <div style={styles.timeRow}>
          <span>{formatTime(currentTime)}</span>
          <span>{duration > 0 ? formatTime(duration) : '--:--'}</span>
        </div>
      </div>

      {/* Controls */}
      <div style={styles.controls}>
        <button
          className="rp-skip-btn"
          onClick={onPrev}
          disabled={!isHost}
          style={{ ...styles.skipBtn, opacity: isHost ? 0.7 : 0.2 }}
          title="Previous"
        >⏮</button>

        <button
          className="rp-play-btn"
          onClick={onTogglePlay}
          disabled={!ready || !isHost}
          style={{
            ...styles.playBtn,
            opacity: ready && isHost ? 1 : 0.45,
            animation: isPlaying && ready ? 'rp-pulse 2s ease-in-out infinite' : 'none',
          }}
          title={isPlaying ? 'Pause' : 'Play'}
        >
          {!ready
            ? <div style={styles.spinner} />
            : isPlaying ? '⏸' : '▶'
          }
        </button>

        <button
          className="rp-skip-btn"
          onClick={onNext}
          disabled={!isHost}
          style={{ ...styles.skipBtn, opacity: isHost ? 0.7 : 0.2 }}
          title="Next"
        >⏭</button>
      </div>

      {!isHost && (
        <p style={styles.hostNote}>Only the host controls playback</p>
      )}
    </div>
  );
}

const styles = {
  wrapper: {
    width: '100%',
  },
  empty: {
    textAlign: 'center',
    padding: '60px 20px',
  },
  emptyIcon: {
    fontSize: 48, marginBottom: 16, opacity: 0.2, color: '#f5e6ed',
  },
  emptyTitle: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: 22, color: 'rgba(245,230,237,0.5)', marginBottom: 8,
  },
  emptySubtitle: {
    fontSize: 12, color: 'rgba(245,230,237,0.3)',
  },

  // Art
  artWrap: {
    position: 'relative', borderRadius: 16,
    overflow: 'hidden', marginBottom: 20,
  },
  art: {
    width: '100%', height: 280,
    objectFit: 'cover', display: 'block',
  },
  artFallback: {
    width: '100%', height: 280,
    background: 'rgba(255,255,255,0.03)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 60, color: 'rgba(245,230,237,0.2)',
  },
  playingBadge: {
    position: 'absolute', top: 12, right: 12,
    background: 'rgba(34,197,94,0.15)',
    border: '0.5px solid rgba(34,197,94,0.4)',
    backdropFilter: 'blur(8px)',
    color: '#22c55e', fontSize: 10,
    letterSpacing: '0.12em', textTransform: 'uppercase',
    borderRadius: 20, padding: '4px 10px',
    display: 'flex', alignItems: 'center', gap: 5,
  },
  playingDot: {
    width: 6, height: 6, borderRadius: '50%',
    background: '#22c55e',
    boxShadow: '0 0 6px #22c55e',
    display: 'inline-block',
  },
  hostBadge: {
    position: 'absolute', top: 12, left: 12,
    background: 'rgba(212,83,126,0.9)',
    color: '#fff', fontSize: 10,
    letterSpacing: '0.1em', textTransform: 'uppercase',
    borderRadius: 20, padding: '4px 10px',
  },
  addedByBadge: {
    position: 'absolute', bottom: 12, left: 12,
    background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)',
    color: 'rgba(245,230,237,0.7)', fontSize: 11,
    borderRadius: 20, padding: '4px 10px',
  },

  // Info
  infoRow: {
    display: 'flex', alignItems: 'flex-start',
    gap: 12, marginBottom: 16,
  },
  trackName: {
    fontFamily: "'Cormorant Garamond', serif",
    fontWeight: 400, fontSize: 24,
    color: '#f5e6ed', marginBottom: 4,
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
  },
  artistName: {
    fontSize: 13, color: 'rgba(245,230,237,0.45)',
  },
  timestampBadge: {
    fontSize: 11, color: '#D4537E',
    marginTop: 4, fontFamily: 'monospace',
  },
  trackCounter: {
    fontSize: 11, color: 'rgba(245,230,237,0.25)',
    fontFamily: 'monospace', letterSpacing: '0.05em',
    flexShrink: 0, paddingTop: 4,
  },

  // Progress
  progressWrap: {
    marginBottom: 20, position: 'relative',
  },
  progressTrack: {
    position: 'relative',
    height: 4, background: 'rgba(255,255,255,0.08)',
    borderRadius: 2, overflow: 'visible', marginBottom: 8,
  },
  progressFill: {
    height: '100%', background: 'linear-gradient(90deg, #993556, #D4537E)',
    borderRadius: 2, transition: 'width 0.5s linear',
    position: 'absolute', top: 0, left: 0,
  },
  progressThumb: {
    position: 'absolute', top: '50%',
    transform: 'translateY(-50%)',
    width: 12, height: 12, borderRadius: '50%',
    background: '#D4537E',
    boxShadow: '0 0 8px rgba(212,83,126,0.7)',
    transition: 'left 0.5s linear',
    pointerEvents: 'none',
  },
  rangeInput: {
    position: 'absolute', top: -6,
    left: 0, width: '100%', height: 16,
    opacity: 0, margin: 0,
    WebkitAppearance: 'none',
  },
  timeRow: {
    display: 'flex', justifyContent: 'space-between',
    fontSize: 10, color: 'rgba(245,230,237,0.3)',
    fontFamily: 'monospace', letterSpacing: '0.05em',
  },

  // Controls
  controls: {
    display: 'flex', alignItems: 'center',
    justifyContent: 'center', gap: 20, marginBottom: 12,
  },
  skipBtn: {
    width: 42, height: 42, borderRadius: '50%',
    background: 'rgba(255,255,255,0.04)',
    border: '0.5px solid rgba(255,255,255,0.08)',
    color: '#f5e6ed', fontSize: 16,
    cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  playBtn: {
    width: 60, height: 60, borderRadius: '50%',
    background: 'linear-gradient(135deg, #993556, #D4537E)',
    color: '#fff', border: 'none',
    fontSize: 22, cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  spinner: {
    width: 20, height: 20, borderRadius: '50%',
    border: '2px solid rgba(255,255,255,0.3)',
    borderTopColor: '#fff',
    animation: 'rp-spin 0.8s linear infinite',
  },
  hostNote: {
    textAlign: 'center', fontSize: 11,
    color: 'rgba(245,230,237,0.22)',
    letterSpacing: '0.08em', marginBottom: 8,
  },
};
